import openai from './openaiClient';
import { supabase } from '../lib/supabase';

/**
 * Service for generating and managing AI-powered trip summaries
 */
export const tripSummaryService = {
  /**
   * Generate an engaging 2-3 sentence narrative summary for a trip
   * @param {Object} tripData - Trip data including destination, activities, duration, etc.
   * @returns {Promise<string>} Generated summary text
   */
  async generateTripSummary(tripData) {
    try {
      // Validate OpenAI is configured
      const apiKey = import.meta.env?.VITE_OPENAI_API_KEY;
      if (!apiKey || apiKey === 'your_vite_openai_api_key') {
        console.warn('OpenAI API key not configured, using default summary generation');
        return this.generateDefaultSummary(tripData);
      }

      // Build a detailed prompt for the AI
      const prompt = this.buildSummaryPrompt(tripData);

      // Call OpenAI API
      const response = await openai?.chat?.completions?.create({
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: 'You are a travel writing expert who creates engaging, concise trip summaries. Write in a warm, inviting tone that makes readers excited about the adventure. Keep summaries to exactly 2-3 sentences.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        max_tokens: 150,
        temperature: 0.7
      });

      const summary = response?.choices?.[0]?.message?.content?.trim();
      
      if (!summary) {
        console.warn('No summary generated from OpenAI, using default');
        return this.generateDefaultSummary(tripData);
      }

      return summary;
    } catch (error) {
      console.error('Error generating trip summary with AI:', error);
      // Fallback to default summary generation
      return this.generateDefaultSummary(tripData);
    }
  },

  /**
   * Build a detailed prompt for AI summary generation
   * @param {Object} tripData - Trip data
   * @returns {string} Formatted prompt
   */
  buildSummaryPrompt(tripData) {
    const parts = [];
    
    // Add destination/park info
    if (tripData?.park) {
      parts?.push(`Create an engaging 2-3 sentence summary for a trip to ${tripData?.park}`);
    } else if (tripData?.destination) {
      parts?.push(`Create an engaging 2-3 sentence summary for a trip to ${tripData?.destination}`);
    } else {
      parts?.push('Create an engaging 2-3 sentence summary for this adventure');
    }

    // Add duration info
    if (tripData?.duration) {
      const days = tripData?.duration;
      const nights = days > 1 ? days - 1 : 0;
      if (nights > 0) {
        parts?.push(`spanning ${days} days and ${nights} night${nights !== 1 ? 's' : ''}`);
      } else {
        parts?.push(`lasting ${days} day${days !== 1 ? 's' : ''}`);
      }
    } else if (tripData?.timeframeText) {
      parts?.push(`during ${tripData?.timeframeText}`);
    }

    // Add activities
    if (tripData?.activities && Array.isArray(tripData?.activities) && tripData?.activities?.length > 0) {
      const activityList = tripData?.activities?.slice(0, 5)?.join(', ');
      parts?.push(`featuring activities like ${activityList}`);
    }

    // Add travelers info if significant
    if (tripData?.travelers && tripData?.travelers > 1) {
      parts?.push(`for ${tripData?.travelers} travelers`);
    }

    // Add accommodation if specified
    if (tripData?.accommodation) {
      parts?.push(`staying at ${tripData?.accommodation}`);
    }

    const prompt = parts?.join('. ') + '. Make it sound exciting and inviting, focusing on the experience and adventure rather than just listing facts.';
    
    return prompt;
  },

  /**
   * Generate a default summary without AI (fallback)
   * @param {Object} tripData - Trip data
   * @returns {string} Default formatted summary
   */
  generateDefaultSummary(tripData) {
    const parts = [];
    
    // Opening sentence with destination
    if (tripData?.park) {
      parts?.push(`Embark on an unforgettable adventure to ${tripData?.park}`);
    } else if (tripData?.destination) {
      parts?.push(`Discover the beauty of ${tripData?.destination}`);
    } else {
      parts?.push('Experience an amazing outdoor adventure');
    }

    // Add duration and activities
    const details = [];
    
    if (tripData?.duration) {
      const days = tripData?.duration;
      const nights = days > 1 ? days - 1 : 0;
      if (nights > 0) {
        details?.push(`${days}-day, ${nights}-night journey`);
      } else {
        details?.push(`${days}-day experience`);
      }
    }

    if (tripData?.activities && Array.isArray(tripData?.activities) && tripData?.activities?.length > 0) {
      const activityCount = tripData?.activities?.length;
      if (activityCount <= 3) {
        details?.push(`featuring ${tripData?.activities?.join(', ')}`);
      } else {
        details?.push(`with ${activityCount} exciting activities including ${tripData?.activities?.slice(0, 3)?.join(', ')}, and more`);
      }
    }

    if (details?.length > 0) {
      parts?.push(` with a ${details?.join(' ')}`);
    }

    // Add travelers info
    if (tripData?.travelers && tripData?.travelers > 1) {
      parts?.push(`. Perfect for groups of ${tripData?.travelers} adventurers seeking memorable experiences together`);
    } else {
      parts?.push('. Create lasting memories in the great outdoors');
    }

    return parts?.join('')?.trim() + '.';
  },

  /**
   * Update trip summary in database
   * @param {string} tripId - Trip ID
   * @param {string} summary - New summary text
   * @returns {Promise<Object>} Updated trip data
   */
  async updateTripSummary(tripId, summary) {
    try {
      // ✅ FIXED: Use explicit column selection to avoid schema cache issues
      const { data, error } = await supabase
        ?.from('trips')
        ?.update({ 
          summary: summary,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', tripId)
        ?.select('id, summary, destination, updated_at') // Explicitly select only needed columns
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating trip summary:', error);
      throw error;
    }
  },

  /**
   * Auto-generate and save summary for a trip
   * @param {string} tripId - Trip ID
   * @param {Object} tripData - Complete trip data
   * @returns {Promise<string>} Generated and saved summary
   */
  async autoGenerateSummary(tripId, tripData) {
    try {
      const summary = await this.generateTripSummary(tripData);
      await this.updateTripSummary(tripId, summary);
      return summary;
    } catch (error) {
      console.error('Error auto-generating trip summary:', error);
      throw error;
    }
  }
};

export default tripSummaryService;